/**
 * Per-REQUEST failure copy for the subscription surface.
 *
 * The rule everywhere in /v2 is: show the backend's own `response.message` — it
 * is already localized via `Accept-Language` and it explains the actual business
 * reason ("you already used your free trial"). But the API does not always send
 * one: a 500, a timeout, a dropped connection, or a bare `{status:false}` all
 * arrive with nothing usable, and the HTTP layer then fills `ApiError.message`
 * with ONE generic line for the whole app.
 *
 * That generic line is what these keys replace. Each entry names the request
 * that actually failed, so a user who loses their connection while cancelling
 * reads "couldn't cancel your subscription" — not the same
 * "an error occurred, try again" they would get from loading invoices, applying
 * a promo code, or starting a trial. One key per live endpoint; no two are the
 * same sentence.
 *
 * Resolution order at every call site is fixed:
 *   `action.apiMessage() ?? translate(action.fallbackKey)`
 *
 * @see ActionState.apiMessage — the backend message, or null when it sent none
 * @see SubscriptionStore — wires one {@link SubscriptionErrorKeys} entry per action
 */
export const SubscriptionErrorKeys = {
  /** `GET /api/v3/subscription` */
  read: 'reference_portal_subscription_err_read',
  /** `GET /api/v3/subscription/plans` */
  plans: 'reference_portal_subscription_err_plans',
  /** `POST /api/v3/subscription/trial` */
  trial: 'reference_portal_subscription_err_trial',
  /** `POST /api/v3/subscription/subscribe` */
  subscribe: 'reference_portal_subscription_err_subscribe',
  /** `POST /api/v3/subscription/subscribe` used for an active subscriber's renewal */
  renew: 'reference_portal_subscription_err_renew',
  /** `POST /api/v3/subscription/reactivate` */
  reactivate: 'reference_portal_subscription_err_reactivate',
  /** `POST /api/v3/subscription/cancel` */
  cancel: 'reference_portal_subscription_err_cancel',
  /** `POST /api/v3/subscription/quote` */
  quote: 'reference_portal_subscription_err_quote',
  /** `POST /api/v3/subscription/auto-renew` */
  autoRenew: 'reference_portal_subscription_err_auto_renew',
  /** `POST /api/v3/subscription/payment-method` */
  paymentMethod: 'reference_portal_subscription_err_payment_method',
  /** `GET /api/v3/subscription/invoices` */
  invoices: 'reference_portal_subscription_err_invoices',
  /** `GET /api/v3/subscription/cards` */
  cards: 'reference_portal_subscription_err_cards',
  /** The post-checkout re-read that confirms whether the charge landed. */
  verify: 'reference_portal_subscription_err_verify',
} as const;

/**
 * Human labels for the log stream — the endpoint each action actually hits, so a
 * failure line reads `POST /api/v3/subscription/trial` and not `trial`.
 */
export const SubscriptionOps = {
  read: 'GET /api/v3/subscription',
  plans: 'GET /api/v3/subscription/plans',
  trial: 'POST /api/v3/subscription/trial',
  subscribe: 'POST /api/v3/subscription/subscribe',
  renew: 'POST /api/v3/subscription/subscribe (renew)',
  reactivate: 'POST /api/v3/subscription/reactivate',
  cancel: 'POST /api/v3/subscription/cancel',
  quote: 'POST /api/v3/subscription/quote',
  autoRenew: 'POST /api/v3/subscription/auto-renew',
  paymentMethod: 'POST /api/v3/subscription/payment-method',
  invoices: 'GET /api/v3/subscription/invoices',
  cards: 'GET /api/v3/subscription/cards',
  verify: 'GET /api/v3/subscription (checkout verify)',
} as const;
